import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import ShowLoader from "@/components/Loader";
import Toaster from "../../components/Toaster";
import { yupResolver } from "@hookform/resolvers/yup";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Input,
  Button,
  Typography,
} from "@material-tailwind/react";
import {
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/solid";
import {
  resetPassword,
  resetStatus,
  verifyToken,
} from "@/store/slices/resetPasswordSlice";
import ScrollToTop from "@/components/ScrollToTop";
import { Header } from ".";

export function ResetPassword() {
  const { t } = useTranslation("resetPassword");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, tokenValid } = useSelector(
    (state) => state.resetPassword
  );

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  /*
   * IMPORTANT:
   * Yup schema is created inside the component so that
   * validation messages update according to the selected language.
   */
  const schema = yup.object({
    token: yup
      .string()
      .required(t("validation.tokenRequired")),

    password: yup
      .string()
      .required(t("validation.passwordRequired"))
      .min(8, t("validation.passwordMin"))
      .matches(/[A-Z]/, t("validation.passwordUppercase"))
      .matches(/[a-z]/, t("validation.passwordLowercase"))
      .matches(/[0-9]/, t("validation.passwordNumber"))
      .matches(
        /[@$!%*?&]/,
        t("validation.passwordSpecial")
      ),

    password_confirmation: yup
      .string()
      .required(t("validation.confirmPasswordRequired"))
      .oneOf(
        [yup.ref("password"), null],
        t("validation.passwordMatch")
      ),
  });

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      token: "",
      password: "",
      password_confirmation: "",
    },
  });

  /*
   * Verify reset token
   */
  useEffect(() => {
    if (!token) {
      navigate("/sign-in");
      return;
    }

    dispatch(verifyToken(token));
  }, [token, dispatch, navigate]);

  /*
   * Handle token validation result
   */
  useEffect(() => {
    if (tokenValid === false) {
      Toaster.error(t("messages.invalidResetLink"));

      navigate("/sign-in");
    } else if (tokenValid === true) {
      setValue("token", token, {
        shouldValidate: true,
      });
    }
  }, [tokenValid, token, setValue, navigate, t]);

  /*
   * Submit reset password
   */
  const onSubmit = async (data) => {
    try {
      const result = await dispatch(
        resetPassword(data)
      ).unwrap();

      if (result.status) {
        Toaster.success(
          t("messages.passwordResetSuccess")
        );

        dispatch(resetStatus());
        reset();

        navigate("/sign-in");
      } else {
        Toaster.error(
          result.message || t("messages.somethingWentWrong")
        );

        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(
        err?.message ||
          t("messages.passwordResetFailed")
      );

      dispatch(resetStatus());
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {loading && <ShowLoader />}

      <ScrollToTop />

      <Header />

      <main
        className="relative flex-grow w-full flex items-center justify-center p-4"
        style={{
          backgroundImage: "url('/img/bgImg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Background Overlay */}
        <div className="absolute inset-0 bg-black opacity-40 z-0"></div>

        {/* Form Card */}
        <div className="relative z-10 w-full max-w-md bg-white rounded-lg shadow-xl p-8">

          {/* Heading */}
          <Typography
            variant="h4"
            color="blue-gray"
            className="mb-6 text-left font-bold"
          >
            {t("title")}
          </Typography>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >

            {/* New Password */}
            <div className="flex flex-col gap-2">

              <Typography
                variant="h6"
                color="blue-gray"
                className="-mb-1"
              >
                {t("newPassword")}
              </Typography>

              <div className="relative">

                <Input
                  type={
                    showPassword
                      ? "text"
                      : "password"
                  }
                  size="lg"
                  {...register("password")}
                  error={Boolean(errors?.password)}
                  placeholder={t("passwordPlaceholder")}
                  className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className:
                      "before:content-none after:content-none",
                  }}
                />

                {showPassword ? (
                  <EyeIcon
                    className="absolute right-2 top-3 w-5 cursor-pointer"
                    onClick={() =>
                      setShowPassword(false)
                    }
                  />
                ) : (
                  <EyeSlashIcon
                    className="absolute right-2 top-3 w-5 cursor-pointer"
                    onClick={() =>
                      setShowPassword(true)
                    }
                  />
                )}

              </div>

              {errors?.password && (
                <p className="text-red-500 text-sm">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="flex flex-col gap-2">

              <Typography
                variant="h6"
                color="blue-gray"
                className="-mb-1"
              >
                {t("confirmPassword")}
              </Typography>

              <div className="relative">

                <Input
                  type={
                    showConfirmPassword
                      ? "text"
                      : "password"
                  }
                  size="lg"
                  {...register(
                    "password_confirmation"
                  )}
                  error={Boolean(
                    errors?.password_confirmation
                  )}
                  placeholder={t("passwordPlaceholder")}
                  className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                  labelProps={{
                    className:
                      "before:content-none after:content-none",
                  }}
                />

                {showConfirmPassword ? (
                  <EyeIcon
                    className="absolute right-2 top-3 w-5 cursor-pointer"
                    onClick={() =>
                      setShowConfirmPassword(false)
                    }
                  />
                ) : (
                  <EyeSlashIcon
                    className="absolute right-2 top-3 w-5 cursor-pointer"
                    onClick={() =>
                      setShowConfirmPassword(true)
                    }
                  />
                )}

              </div>

              {errors?.password_confirmation && (
                <p className="text-red-500 text-sm">
                  {
                    errors.password_confirmation
                      .message
                  }
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              disabled={loading}
              className="btn-material bg-black hover:bg-gray-800 text-base py-3"
            >
              {t("submit")}
            </Button>

          </form>
        </div>

        {/* Copyright */}
        <Typography
          variant="small"
          className="absolute bottom-1 mt-0 left-0 right-0 text-center text-white/80 z-10"
        >
          {t("copyright")}
        </Typography>
      </main>
    </div>
  );
}

export default ResetPassword;